/**
 * Air-vent traversal (Phase 8). The ductwork the HVAC service already runs
 * through the building becomes a way to move through it.
 *
 * The grilles are not new geometry — they're the HVAC service's outlets, placed
 * back in 2c-ii and until now purely decorative. Entering one crawls you to
 * another grille on the same floor, which is the stealth value: a duct doesn't
 * care about locked doors, patrolled corridors, or who's watching the room you
 * need to cross.
 *
 * Two conditions, both of which make it a decision rather than a free teleport:
 *   - You must be CROUCHED. It's a duct.
 *   - It takes time (a channel) and makes noise at BOTH ends — someone hears
 *     you get in, and someone hears you get out.
 *
 * And the reason to sabotage the air handling at last: with HVAC tampered the
 * fans are stopped, so crawling is quieter and quicker. Until now cutting it
 * only set a flag.
 *
 * Pure + headless (rule 4).
 */

import { CONFIG } from '../config';
import type { Service, ServiceOutlet } from './dungeon/services';

export interface VentNetwork {
  /** Grilles on the active floor, in a stable order. */
  grilles: ServiceOutlet[];
  /** The fans are off (HVAC sabotaged) — quieter and faster to crawl. */
  fansOff: boolean;
}

/** The ducts reachable on `floorIdx`, or null when the building has no HVAC. */
export function ventNetwork(services: readonly Service[], floorIdx: number): VentNetwork | null {
  const hvac = services.find((s) => s.kind === 'hvac');
  if (!hvac) return null;
  const grilles = hvac.outlets
    .filter((o) => o.floorIdx === floorIdx)
    .slice()
    .sort((a, b) => a.y - b.y || a.x - b.x); // stable, so a crawl is repeatable
  return { grilles, fansOff: hvac.state === 'tampered' };
}

/** The grille the player is standing on, or null. */
export function grilleAt(net: VentNetwork | null, x: number, y: number): ServiceOutlet | null {
  return net?.grilles.find((g) => g.x === x && g.y === y) ?? null;
}

/**
 * Where crawling in at `from` puts you: the nearest OTHER grille on the floor.
 * Nearest rather than a cycle, because a duct you can't predict is a gamble
 * instead of a route — the player should be able to look at two grilles and
 * know they connect.
 */
export function ventExit(net: VentNetwork, from: ServiceOutlet): ServiceOutlet | null {
  let best: ServiceOutlet | null = null;
  let bestD = Infinity;
  for (const g of net.grilles) {
    if (g.x === from.x && g.y === from.y) continue;
    const d = Math.abs(g.x - from.x) + Math.abs(g.y - from.y);
    if (d < bestD) { bestD = d; best = g; }
  }
  return best;
}

/** Seconds this crawl takes — fans off is quicker as well as quieter. */
export function crawlSeconds(net: VentNetwork): number {
  const cfg = CONFIG.vent;
  return net.fansOff ? cfg.crawlSec * cfg.fansOffMult : cfg.crawlSec;
}

/** How loud getting in (or out) is. */
export function crawlLoudness(net: VentNetwork): number {
  const cfg = CONFIG.vent;
  return net.fansOff ? cfg.loudness * cfg.fansOffMult : cfg.loudness;
}

/** Can the player enter this grille right now? Crouch-only — it's a duct. */
export function canEnterVent(
  net: VentNetwork | null,
  x: number,
  y: number,
  stance: string,
): boolean {
  if (stance !== 'crouch') return false;
  const g = grilleAt(net, x, y);
  return g !== null && net !== null && ventExit(net, g) !== null;
}
